import { resolvePrintBlockRole, type PrintBlockRole } from './printBlockRole';

/**
 * Momentos pedagógicos — variación visual acotada (Fase 7).
 * No inventa didáctica: solo tipifica el layout según posición / rol / purpose.
 */
export type PrintPedagogicalMoment =
  | 'activation'
  | 'exploration'
  | 'practice'
  | 'closure';

const CLOSURE_PURPOSE_RE =
  /closure|cierre|reflect|reflection|metacogn|ticket|sintesis|s[ií]ntesis/i;

/**
 * @param blocks Lista de bloques de la guía
 * @param index Índice del bloque actual
 * @param purpose `_blueprintPurpose` o equivalente (opcional)
 */
export function resolvePrintPedagogicalMoment(
  blocks: Array<{ mode?: string; _blueprintPurpose?: string }>,
  index: number,
  purpose = '',
): PrintPedagogicalMoment {
  const total = blocks.length;
  if (total === 0 || index < 0 || index >= total) return 'exploration';

  const role = resolvePrintBlockRole(blocks[index]?.mode || '');
  const purposeText = String(
    purpose || blocks[index]?._blueprintPurpose || '',
  ).trim();

  if (CLOSURE_PURPOSE_RE.test(purposeText)) return 'closure';

  if (role === 'practice') return 'practice';

  const firstPractice = blocks.findIndex(
    (block) => resolvePrintBlockRole(block.mode || '') === 'practice',
  );
  const lastPractice = (() => {
    for (let i = blocks.length - 1; i >= 0; i -= 1) {
      if (resolvePrintBlockRole(blocks[i]?.mode || '') === 'practice') return i;
    }
    return -1;
  })();

  // Tras la última práctica: cierre (reflexión, destacado final, etc.).
  if (lastPractice >= 0 && index > lastPractice) return 'closure';

  // Último bloque de una guía sin práctica (p. ej. atlas corto) → cierre suave.
  if (firstPractice < 0 && index === total - 1 && total > 1) return 'closure';

  // Antes de la práctica (o guías solo de lectura): primera pieza = activación.
  if (firstPractice < 0 || index < firstPractice) {
    const teachingBefore = blocks
      .slice(0, firstPractice < 0 ? total : firstPractice)
      .map((block, i) => ({ block, i }))
      .filter(({ block }) => resolvePrintBlockRole(block.mode || '') === 'teaching');
    if (teachingBefore[0]?.i === index) return 'activation';
    return 'exploration';
  }

  // Enseñanza intercalada entre prácticas → exploración.
  if (role === 'teaching') return 'exploration';

  return 'exploration';
}

export function pedagogicalMomentClass(moment: PrintPedagogicalMoment): string {
  return `print-block--moment-${moment}`;
}

/** Etiqueta opcional para divisores de sección — no sellos por bloque. */
export function pedagogicalMomentSectionLabel(
  moment: PrintPedagogicalMoment,
): { label: string; hint: string } | null {
  switch (moment) {
    case 'practice':
      return {
        label: 'A practicar',
        hint: 'Aplica lo que acabas de ver',
      };
    case 'closure':
      return {
        label: 'Para cerrar',
        hint: 'Síntesis y mirada metacognitiva',
      };
    default:
      return null;
  }
}

export function shouldShowMomentSectionDivider(
  moments: PrintPedagogicalMoment[],
  index: number,
): boolean {
  if (index <= 0 || index >= moments.length) return false;
  const current = moments[index];
  const prev = moments[index - 1];
  if (current === prev) return false;
  return current === 'practice' || current === 'closure';
}

export type { PrintBlockRole };
